// POST to one of our edge functions and hand back its JSON body, or throw an
// Error whose message is the function's OWN sentence ({ error: '…' }).
// Extracted from inviteMember on 2026-09-29 so the admin app's tenant wizard
// (the provision-* functions) makes the call the same way.
//
// Direct REST POST, NOT supabase.functions.invoke(): that path takes the
// navigator.locks auth-token lock that token refresh and background queries
// also hold, and an invite stuck on "Sending…" never reached the function's
// first line (2026-05-27, project_auth_direct_rest_bypass). Same shape as
// Login.tsx's signInDirect and Dashboard.tsx's tenant switcher. Reading the
// body ourselves also means the server's reason always reaches the screen.
import { supabase } from '@/lib/supabase'
import { errorText } from '@/lib/errorText'

export interface EdgePostOpts {
  /** Wall clock before giving up, so a hang surfaces instead of a spinner. */
  timeoutMs:      number
  /** The sentence for a timeout: what to do next. */
  timeoutMessage: string
  /** The fallback when the function fails without saying why. */
  failMessage:    string
  /** How the expired-session sentence ends: "…sign in again, then <this>."
   *  The manuals quote the invite one ("re-send the invite") word for word. */
  retryAction:    string
  /** Most of our functions answer `{ ok: true, … }`, and a 200 without it is
   *  a failure. A few return their payload bare (platform-cost-snapshot):
   *  pass false for those, and a 2xx is success. */
  requireOk?:     boolean
}

export async function postEdgeFunction<T extends { ok?: boolean; error?: unknown }>(
  fn: string, body: Record<string, unknown>, opts: EdgePostOpts,
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated — refresh and try again')
  // getSession() returns the stored token WITHOUT refreshing — if the
  // background refresh timer was paused (backgrounded tab) the token can be
  // expired, and the edge function rejects it with a cryptic "Invalid
  // session". Pre-empt that with an actionable message rather than sending a
  // doomed request. (A token refresh here would take the auth web-lock we
  // deliberately bypass above, so we tell the operator to reload instead.)
  if (session.expires_at && session.expires_at * 1000 <= Date.now()) {
    throw new Error(`Your session has expired — refresh the page, sign in again, then ${opts.retryAction}.`)
  }
  const url     = import.meta.env.VITE_SUPABASE_URL as string
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  const ctrl = new AbortController()
  const timeoutId = setTimeout(() => ctrl.abort(), opts.timeoutMs)
  let resp: Response
  try {
    resp = await fetch(`${url}/functions/v1/${fn}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': anonKey, 'Authorization': `Bearer ${session.access_token}` },
      body:    JSON.stringify(body),
      signal:  ctrl.signal,
    })
  } catch (e) {
    if ((e as { name?: string })?.name === 'AbortError') throw new Error(opts.timeoutMessage)
    throw e
  } finally {
    clearTimeout(timeoutId)
  }
  const out = await resp.json().catch(() => null) as T | null
  // errorText() not `out?.error ?? …`: if the server/gateway ever puts a
  // non-string in `.error`, `new Error(obj)` renders "[object Object]" and
  // buries the real reason (Sentry e3cfcff7, 2026-06-02).
  if (!resp.ok) throw new Error(errorText(out?.error, `${opts.failMessage} (HTTP ${resp.status})`))
  if (!out || (opts.requireOk !== false && !out.ok)) throw new Error(errorText(out?.error, opts.failMessage))
  return out
}
